import React, { useState } from 'react';
import {
  DefaultButton,
  IconButton,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  PrimaryButton,
  Spinner,
  Dialog,
  DialogFooter,
  DialogType,
  DirectionalHint,
} from 'office-ui-fabric-react';
import { FieldArray, Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { get, isEqual } from 'lodash';
import { denormalize } from 'normalizr';
import clsx from 'clsx';
import { useBoolean } from '@uifabric/react-hooks';
import TextButton from '../../components/TextButton';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleContent from '../../components/SubModuleContent';
import SubModuleHeader from '../../components/SubModuleHeader';
import Main from '../../components/Main';
import PageTitle from '../../components/PageTitle';
import Callout from '../../components/Callout';
import AgreementCard from '../../components/AgreementCard';
import styles from './AddAgreement.style';
import useAPI from '../../hooks/useAPI';
import {
  fetchContract,
  reorderAgreements,
  createFallbackAgreement,
  updateFallbackAgreement,
} from '../../actions/contracts';
import { contractSchema } from '../../actions/schemas';
import { fetchCalculationTypes } from '../../actions/calculationTypes';
import { createAgreement, editAgreement } from '../../actions/agreements';
import RoyaltyFeeForm from '../../components/RoyaltyFeeForm';
import SavingIndicator from '../../components/SavingIndicator';
import UnassignedRoyaltyFeeForm from '../../components/UnassignedRoyaltyFeeForm';
import { getPrefixSentence } from '../../components/FilterDetails';

function formFallbackAgreementToApiPayload(fallbackAgreement, selectedCalculation) {
  if (!fallbackAgreement || !selectedCalculation) {
    return fallbackAgreement;
  }

  const calcParams = selectedCalculation.calcParams.map(calcParam => ({
    property: calcParam,
    value: get(fallbackAgreement, `calcParams.${calcParam}`),
  }));

  return {
    calculation: selectedCalculation?.slug,
    calcParams,
  };
}

const dialogContentProps = {
  type: DialogType.normal,
  title: 'Pausing Contract',
  subText: 'This action will pause the contract. Are you sure you would like to continue?',
};

const AddAgreement = () => {
  const history = useHistory();
  const { contractAddress } = useParams();
  const [selectedAgreement, setSelectedAgreement] = useState(null);

  const [formData, setFormData] = useState({});
  const [dialogHidden, { setTrue: hideDialog, setFalse: showDialog }] = useBoolean(true);
  const [addFeeDialogHidden, { setTrue: hideFeeDialog, setFalse: showFeeDialog }] =
    useBoolean(true);
  const [
    addUnassignedFeeDialogHidden,
    { setTrue: hideUnassignedFeeDialog, setFalse: showUnassignedFeeDialog },
  ] = useBoolean(true);

  const calculationTypes = useAPI(() => fetchCalculationTypes(), [], {
    fetchOnMount: true,
    selector: state =>
      Object.values(state.entities?.calculationTypes || {}).map(calcType => ({
        ...calcType,
        key: calcType.slug,
        text: calcType.name,
      })),
  });
  const contract = useAPI(() => fetchContract(contractAddress), [contractAddress], {
    fetchOnMount: true,
    selector: state =>
      denormalize(
        get(state, `entities.contracts.${contractAddress}`, []),
        contractSchema,
        state.entities || {},
      ),
  });
  const reorderAgreementsApi = useAPI(
    requestBody => reorderAgreements(contract.data?.address, requestBody),
    [contract],
    {
      fetchOnMount: false,
      autoFetch: false,
      onSuccessCallback() {
        if (window.config.enableContractAdvanceGroups) {
          history.push(`/contracts/${contract.data.address}/add-advancegroup`);
        } else {
          history.push(`/contracts/${contract.data.address}/`);
        }
      },
    },
  );

  const createOrUpdateFallbackAgreementAPI = useAPI(
    (formValues, isFallbackPresentForContract) => {
      if (isFallbackPresentForContract) {
        return updateFallbackAgreement(contractAddress, formValues);
      }

      return createFallbackAgreement(contractAddress, formValues);
    },
    [contractAddress],
    {
      fetchOnMount: false,
      autoFetch: false,
    },
  );

  const initialValues = {
    agreements: contract.data?.agreements || [],
    // fallbackAgreement: fallbackAgreementToFormData(contract.data?.fallbackAgreement),
  };

  const createOrUpdateAgreementAPI = useAPI(
    (formValues, agreementAddress) => {
      if (agreementAddress) {
        return editAgreement(contractAddress, agreementAddress, formValues);
      }

      return createAgreement(contractAddress, formValues);
    },
    [contractAddress],
    {
      fetchOnMount: false,
      autoFetch: false,
    },
  );

  const [isRoyaltyFeePanelOpen, { setTrue: openAddFeePanel, setFalse: dismissAddFeePanel }] =
    useBoolean(false);

  const [
    isUnassignedRoyaltyFeePanelOpen,
    { setTrue: openAddUnassignedFeePanel, setFalse: dismissAddUnassignedFeePanel },
  ] = useBoolean(false);

  const onUnassignedFeePanelDismissed = () => {
    dismissAddUnassignedFeePanel();
  };

  const editFeeClicked = agreement => {
    setSelectedAgreement(agreement);
    openAddFeePanel();
  };

  const onFeePanelDismissed = () => {
    setSelectedAgreement(null);
    dismissAddFeePanel();
  };
  const saveFeeToApi = async formValues => {
    await createOrUpdateAgreementAPI.fetchApi(formValues, selectedAgreement?.address);
    dismissAddFeePanel();
  };

  const saveUnassignedFeeToApi = async formValues => {
    const selectedCalculation = calculationTypes.data?.find(
      calcType => calcType.slug === formValues.fallbackAgreement?.calculation,
    );

    await createOrUpdateFallbackAgreementAPI.fetchApi(
      formFallbackAgreementToApiPayload(formValues.fallbackAgreement, selectedCalculation),
      contract.data?.fallbackAgreement,
    );

    dismissAddUnassignedFeePanel();
  };

  const handleSaveUnassignedFee = formValues => {
    if (contract.data?.approvalStatus?.toLowerCase() === 'approved') {
      setFormData(formValues);
      showUnassignedFeeDialog();
    } else {
      saveUnassignedFeeToApi(formValues).catch();
    }
  };

  const handleSaveFee = formValues => {
    if (contract.data?.approvalStatus?.toLowerCase() === 'approved') {
      setFormData(formValues);
      showFeeDialog();
    } else {
      saveFeeToApi(formValues).catch();
    }
  };

  function getErrorMessage(error) {
    if (error.status === 404) {
      return 'Contract was not found. Please try a different link.';
    }

    return error.message;
  }

  function showReorderButton({ agreements }) {
    if (!agreements || agreements.length < 2) {
      return false;
    }

    const newOrder = agreements.map(agreement => agreement.address);
    const existingOrder = [...contract.data?.agreements]
      .sort((a, b) => a.order - b.order)
      .map(agreement => agreement.address);

    return !isEqual(newOrder, existingOrder);
  }

  const submitToApi = async formValues => {
    await reorderAgreementsApi.fetchApi({
      agreementsOrder: formValues.agreements.map(agreement => agreement.address),
    });
  };

  const onSubmit = formValues => {
    if (contract.data.approvalStatus && contract.data.approvalStatus.toLowerCase() === 'approved') {
      setFormData(formValues);
      showDialog();
    } else {
      submitToApi(formValues).catch();
    }
  };

  if (contract.isLoading) {
    return (
      <>
        <Spinner className={styles.spinner} data-testid="agreementSpinner" />
      </>
    );
  }

  const handleCancel = () => {
    // Re-direct to contract details page
    if (window.config.enableContractAdvanceGroups) {
      history.push(`/contracts/${contractAddress}/add-advancegroup`);
    } else {
      history.push(`/contracts/${contractAddress}`);
    }
  };

  const renderAddRoyaltyFeeHeader = (props, defaultRenderer) => {
    return (
      <>
        <SubModuleHeader className={styles.addRoyaltyFeeHeaderContainer}>
          <div className={styles.addRoyaltyFeeHeader}>
            <PageTitle>
              {selectedAgreement ? 'Edit Royalty Fee' : 'Add a Royalty Fee'}
              <Callout
                directionalHint={DirectionalHint.bottomCenter}
                content="Rates are executed in order from top to bottom. To change the order, select the row(s) you want to move and drag and drop them into place."
              />
            </PageTitle>
          </div>
          <div className={styles.addRoyaltyFeeExitButton}>{defaultRenderer(props)}</div>
        </SubModuleHeader>
      </>
    );
  };

  if (contract.error) {
    return (
      <>
        <PageTitle>Whoops!</PageTitle>
        <Module>
          <ModuleContent>
            <SubModule>
              <SubModuleContent>{getErrorMessage(contract.error)}</SubModuleContent>
            </SubModule>
          </ModuleContent>
        </Module>
      </>
    );
  }

  const formatErrorMessage = ({ message, secondaryMessage }) =>
    secondaryMessage ? `${message}. ${secondaryMessage}` : message;

  const agreementCreateOrAddErrorMessage = error => {
    return error.message
      ? formatErrorMessage(error)
      : 'Something went wrong when saving. Please try refreshing the page.';
  };

  return (
    <>
      {createOrUpdateAgreementAPI.error && (
        <MessageBar
          isMultiline={false}
          messageBarType={MessageBarType.error}
          messageBarIconProps={{ iconName: 'Info' }}
        >
          {agreementCreateOrAddErrorMessage(createOrUpdateAgreementAPI.error)}
        </MessageBar>
      )}
      <PageTitle>
        Royalty Fees
        <Callout content="Fee and rate calculations are executed in order from top to bottom. Use the up and down arrow buttons to rearrange the royalty fee cards." />
      </PageTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({ values, isSubmitting, setSubmitting }) => (
          <>
            <Form>
              <Main>
                {/* AGREEMENTS LIST */}
                <FieldArray name="agreements" required>
                  {({ swap }) =>
                    values.agreements.map((agreement, index) => (
                      <AgreementCard
                        key={agreement.address}
                        agreement={agreement}
                        enableUpArrow={!isSubmitting && index > 0}
                        enableDownArrow={
                          !isSubmitting && index < contract.data?.agreements.length - 1
                        }
                        upArrowClicked={() => swap(index, index - 1)}
                        downArrowClicked={() => swap(index, index + 1)}
                        editFeeClicked={editFeeClicked}
                      />
                    ))
                  }
                </FieldArray>

                {/* ADD A FEE SECTION */}
                <Module className={styles.cardSection}>
                  <ModuleContent>
                    <SubModule
                      disableAccordian
                      closed
                      className={clsx(
                        styles.addFeeButtonSectionHeader,
                        isSubmitting && styles.addFeeButtonSectionHeaderDisabled,
                      )}
                      disabled={isSubmitting}
                      onClick={openAddFeePanel}
                    >
                      <SubModuleHeader
                        variant="large"
                        extra={
                          <IconButton
                            iconProps={{ iconName: 'Add' }}
                            title="Add a Royalty Fee"
                            ariaLabel="Add a Royalty Fee"
                          />
                        }
                      >
                        Add a Royalty Fee
                      </SubModuleHeader>

                      {/* only render the panel if it's open. This will mount & unmount the form which resets the state so we don't have to */}
                      {isRoyaltyFeePanelOpen && (
                        <Panel
                          isOpen={isRoyaltyFeePanelOpen}
                          onDismiss={onFeePanelDismissed}
                          type={PanelType.smallFluid}
                          closeButtonAriaLabel="Close"
                          onRenderHeader={renderAddRoyaltyFeeHeader}
                          onOuterClick={() => {}}
                          focusTrapZoneProps={{ disableFirstFocus: true }}
                        >
                          <RoyaltyFeeForm
                            agreement={selectedAgreement}
                            contractAddress={contractAddress}
                            onCancel={onFeePanelDismissed}
                            onSubmit={handleSaveFee}
                            isCreatingAgreement={createOrUpdateAgreementAPI.isLoading}
                          />
                        </Panel>
                      )}
                    </SubModule>
                  </ModuleContent>
                </Module>

                {/* ROYALTY FEE FOR UNASSIGNED SECTION (DETAILS + PANEL) */}
                {window.config.enableFallbackAgreement && (
                  <Module className={styles.cardSection}>
                    <ModuleContent>
                      {contract.data?.fallbackAgreement ? (
                        <SubModule disableAccordian closed>
                          <SubModuleHeader
                            extra={
                              <TextButton onClick={() => openAddUnassignedFeePanel()}>
                                Edit Fee
                              </TextButton>
                            }
                          >
                            Royalty Fee for Unassigned:{' '}
                            {getPrefixSentence(
                              contract.data.fallbackAgreement.calculation,
                              contract.data.fallbackAgreement,
                            )}
                          </SubModuleHeader>
                        </SubModule>
                      ) : (
                        <SubModule
                          disableAccordian
                          closed
                          onClick={openAddUnassignedFeePanel}
                          className={clsx(
                            styles.addFeeButtonSectionHeader,
                            isSubmitting && styles.addFeeButtonSectionHeaderDisabled,
                          )}
                          disabled={isSubmitting}
                        >
                          <SubModuleHeader
                            variant="large"
                            extra={
                              <IconButton
                                data-testid="addUnassignedFee"
                                iconProps={{ iconName: 'Add' }}
                                title="Add Royalty Fee for Unassigned"
                                ariaLabel="Add Royalty Fee for Unassigned"
                              />
                            }
                          >
                            Add Royalty Fee for Unassigned
                          </SubModuleHeader>
                        </SubModule>
                      )}

                      {/* Panel stays outside the condition, since we open it in add and edit cases */}
                      {isUnassignedRoyaltyFeePanelOpen && (
                        <Panel
                          isOpen={isUnassignedRoyaltyFeePanelOpen}
                          onDismiss={onUnassignedFeePanelDismissed}
                          type={PanelType.smallFluid}
                          closeButtonAriaLabel="Close"
                          onOuterClick={() => {}}
                        >
                          <UnassignedRoyaltyFeeForm
                            calculationTypes={calculationTypes}
                            fallbackAgreement={values.fallbackAgreement}
                            isSubmitting={isSubmitting}
                            onCancel={onUnassignedFeePanelDismissed}
                            onSubmit={handleSaveUnassignedFee}
                            isCreatingFallbackAgreement={
                              createOrUpdateFallbackAgreementAPI.isLoading
                            }
                          />
                        </Panel>
                      )}
                    </ModuleContent>
                  </Module>
                )}

                {/* BUTTONS */}
                <div className={styles.buttonContainer}>
                  <TextButton
                    className={styles.cancelButton}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </TextButton>

                  {
                    // eslint-disable-next-line no-nested-ternary
                    showReorderButton(values) ? (
                      isSubmitting ? (
                        <SavingIndicator />
                      ) : (
                        <PrimaryButton
                          type="submit"
                          disabled={isSubmitting}
                          data-testid="addAgreementSubmitButton"
                        >
                          Apply Re-order
                        </PrimaryButton>
                      )
                    ) : (
                      <PrimaryButton
                        data-testid="addAgreementContinueButton"
                        onClick={handleCancel}
                      >
                        Continue
                      </PrimaryButton>
                    )
                  }
                </div>
              </Main>
            </Form>
            <Dialog hidden={dialogHidden} dialogContentProps={dialogContentProps}>
              <DialogFooter>
                <PrimaryButton
                  text="Continue"
                  onClick={() => {
                    hideDialog();
                    submitToApi(formData).catch();
                  }}
                />
                <DefaultButton
                  text="Cancel"
                  onClick={() => {
                    hideDialog();
                    setSubmitting(false);
                  }}
                />
              </DialogFooter>
            </Dialog>
          </>
        )}
      </Formik>
      <Dialog hidden={addFeeDialogHidden} dialogContentProps={dialogContentProps}>
        <DialogFooter>
          <PrimaryButton
            text="Continue"
            data-testid="addFeeConfirmation"
            onClick={() => {
              hideFeeDialog();
              saveFeeToApi(formData).catch();
            }}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => {
              hideFeeDialog();
            }}
          />
        </DialogFooter>
      </Dialog>

      <Dialog hidden={addUnassignedFeeDialogHidden} dialogContentProps={dialogContentProps}>
        <DialogFooter>
          <PrimaryButton
            text="Continue"
            onClick={() => {
              hideUnassignedFeeDialog();
              saveUnassignedFeeToApi(formData).catch();
            }}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => {
              hideUnassignedFeeDialog();
            }}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default AddAgreement;
